import moment from 'moment'
import { useMemo, useState, useCallback } from 'react'
import ProductsCombo from '@/widgets/ProductsCombo'
import { showError } from '@/lib/dialogHelper'

const UsageIcon = () => (
	<svg
		className="h-6 w-6 text-indigo-400"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
		style={{ opacity: 0.75 }}
	>
		<path
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M12 6v6l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
		/>
	</svg>
)

export default function ConsumptionPanel({
	membershipId,
	remainingUsage = 0,
	history = [],
	onConsume,
	disabled = false,
}) {
	// Default datetime-local value: now (once)
	const [date, setDate] = useState(() => moment().format('YYYY-MM-DD HH:mm'))
	const [service, setService] = useState('')
	const [selectedProductId, setSelectedProductId] = useState(null)
	const [saving, setSaving] = useState(false)

	const items = useMemo(() => {
		const arr = Array.isArray(history) ? history : []
		const mapped = arr.map(x => (typeof x === 'string' ? { date: x, service: '' } : x))
		return mapped.sort((a, b) => {
			const dateA = moment(a.date)
			const dateB = moment(b.date)
			if (!dateA.isValid() && !dateB.isValid()) return 0
			if (!dateA.isValid()) return 1
			if (!dateB.isValid()) return -1
			return dateB.valueOf() - dateA.valueOf()
		})
	}, [history])

	// Stable consume handler
	const handleConsumeClick = useCallback(async () => {
		setSaving(true)
		try {
			const payload = {
				membershipId,
				date: moment(date).isValid() ? moment(date).format('YYYY-MM-DD HH:mm') : '',
				service,
			}
			const res = await onConsume?.(payload)
			if (res?.success) {
				setService('')
				setSelectedProductId(null)
			} else {
				showError(res?.error || 'Failed to record consumption.')
			}
		} finally {
			setSaving(false)
		}
	}, [membershipId, date, service, onConsume])

	let canConsume = !disabled && !saving && remainingUsage > 0 && selectedProductId !== null
	// console.log(`TCL -> ConsumptionPanel.jsx:73 -> ConsumptionPanel -> canConsume:`, canConsume)

	return (
		<div className="flex h-full max-h-[550px] w-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
			<div className="border-b border-gray-200 p-6">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold text-gray-900">Usage consumption</h2>
					<span
						className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
							remainingUsage > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
						}`}
					>
						Left: {remainingUsage}
					</span>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto lg:p-2 xl:p-6">
				<div className="mb-6 space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Date &amp; time</label>
						<input
							type="datetime-local"
							value={date}
							onChange={e => setDate(e.target.value)}
							disabled={disabled || saving}
							className={`block w-full rounded-lg border-0 py-2.5 px-3.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 ${
								disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
							}`}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
						<ProductsCombo
							selectedId={selectedProductId}
							onChange={(product) => { setService(product?.name || ''); setSelectedProductId(product?._id); }}
							placeholder="Select or create a product..."
						/>
					</div>
					<div className="flex">
						<button
							type="button"
							onClick={handleConsumeClick}
							disabled={!canConsume}
							className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
								'bg-indigo-600 text-white hover:bg-indigo-700'
							}`}
						>
							{saving ? 'Consuming...' : 'Use'}
							{!canConsume && <span className="ml-2 text-sm">(Disabled)</span>}
						</button>
					</div>
				</div>

				{items.length > 0 ? (
					<div className="border-t border-gray-200 divide-y divide-gray-200">
						{items.map((c, idx) => {
							const key = c._id || `${c.date ?? 'na'}-${idx}`
							const formattedDate = moment(c?.date).isValid()
								? moment(c.date).format('YYYY-MM-DD HH:mm')
								: '—'
							return (
								<div key={key} className="flex items-start gap-3 py-4">
									<div className="flex-shrink-0 pt-1 text-indigo-500">
										<UsageIcon />
									</div>
									<div className="min-w-0 flex-1">
										<p className="text-sm font-semibold text-gray-900">
											{formattedDate}
											{c?.userName ? ` · ${c.userName}` : ''}
										</p>
										<p className="mt-1 text-sm leading-relaxed text-gray-600 whitespace-pre-wrap">
											{c?.service || '—'}
										</p>
									</div>
								</div>
							)
						})}
					</div>
				) : (
					<div className="text-center py-8 text-gray-500">
						<p className="text-sm">No consumption history.</p>
					</div>
				)}
			</div>
		</div>
	)
}
